/*
 * ppdc — a small runtime that renders Claude Design ".dc.html" templates with Preact.
 *
 * It mirrors the semantics of the design tool's runtime (support.js) closely enough
 * that the prototype markup renders identically, but without React/Babel from a CDN:
 *   {{ path }} interpolation (paths, ===/!==/==/!=, !negation, literals)
 *   <sc-if value>, <sc-for list as>, <dc-import name ...props>
 *   style-hover / style-focus … pseudo-class attributes
 *   logic classes (class Component extends DCLogic) with setState/props/lifecycle
 *
 * Requires window.preact (preact.min.umd.js).
 */
(function () {
  'use strict';
  var P = window.preact;
  var h = P.h, Fragment = P.Fragment;

  // ---------- expressions (same grammar as the design runtime) ----------
  var IDENT_RE = /^[A-Za-z_$][A-Za-z0-9_$]*/;
  var NUMBER_RE = /^-?\d+(\.\d+)?$/;
  function resolve(vals, src) {
    var expr = String(src).trim();
    if (!expr) return undefined;
    if (expr[0] === '(' && expr[expr.length - 1] === ')' && parensWrapWhole(expr)) return resolve(vals, expr.slice(1, -1));
    var eq = findTopLevelEquality(expr);
    if (eq) {
      var lv = resolve(vals, expr.slice(0, eq.index)), rv = resolve(vals, expr.slice(eq.index + eq.op.length));
      switch (eq.op) { case '===': return lv === rv; case '!==': return lv !== rv; case '==': return lv == rv; default: return lv != rv; }
    }
    if (expr[0] === '!') return !resolve(vals, expr.slice(1));
    if (expr === 'true') return true;
    if (expr === 'false') return false;
    if (expr === 'null') return null;
    if (expr === 'undefined') return undefined;
    if (NUMBER_RE.test(expr)) return Number(expr);
    if (expr.length >= 2 && (expr[0] === '"' || expr[0] === "'") && expr[expr.length - 1] === expr[0]) return expr.slice(1, -1);
    return resolvePath(vals, expr);
  }
  function parensWrapWhole(expr) {
    var d = 0;
    for (var i = 0; i < expr.length - 1; i++) { if (expr[i] === '(') d++; else if (expr[i] === ')') { d--; if (d === 0) return false; } }
    return true;
  }
  function findTopLevelEquality(expr) {
    var d = 0;
    for (var i = 0; i < expr.length; i++) {
      var c = expr[i];
      if (c === '[' || c === '(') d++;
      else if (c === ']' || c === ')') d--;
      else if (d === 0 && (c === '=' || c === '!') && expr[i + 1] === '=') {
        if (i > 0 && (expr[i - 1] === '=' || expr[i - 1] === '!')) continue;
        if (!expr.slice(0, i).trim()) continue;
        return { index: i, op: expr[i + 2] === '=' ? c + '==' : c + '=' };
      }
    }
    return null;
  }
  function resolvePath(vals, expr) {
    var head = expr.match(IDENT_RE);
    if (!head) return undefined;
    var cur = vals == null ? undefined : vals[head[0]], i = head[0].length;
    while (i < expr.length) {
      if (expr[i] === '.') {
        var m = expr.slice(i + 1).match(IDENT_RE) || expr.slice(i + 1).match(/^\d+/);
        if (!m) return undefined;
        cur = cur == null ? undefined : cur[m[0]];
        i += 1 + m[0].length;
      } else if (expr[i] === '[') {
        var depth = 1, j = i + 1;
        while (j < expr.length && depth > 0) { if (expr[j] === '[') depth++; else if (expr[j] === ']') { depth--; if (depth === 0) break; } j++; }
        if (depth !== 0) return undefined;
        var key = resolve(vals, expr.slice(i + 1, j));
        cur = cur == null ? undefined : cur[key];
        i = j + 1;
      } else return undefined;
    }
    return cur;
  }
  function compileAttr(raw) {
    var whole = raw.match(/^\s*\{\{([\s\S]+?)\}\}\s*$/);
    if (whole) { var path = whole[1]; return function (v) { return resolve(v, path); }; }
    if (raw.indexOf('{{') >= 0) {
      var parts = raw.split(/\{\{([\s\S]+?)\}\}/g);
      return function (v) { return parts.map(function (s, i) { if (i & 1) { var r = resolve(v, s); return r == null ? '' : r; } return s; }).join(''); };
    }
    return function () { return raw; };
  }

  // ---------- HTML encoding (keep camelCase attribute names through the HTML parser) ----------
  var CAMEL = 'sc-camel-';
  var RAW_WRAP = { select: 'sc-raw-select', table: 'sc-raw-table', tbody: 'sc-raw-tbody', thead: 'sc-raw-thead', tfoot: 'sc-raw-tfoot', tr: 'sc-raw-tr', td: 'sc-raw-td', th: 'sc-raw-th', caption: 'sc-raw-caption' };
  var RAW_UNWRAP = {}; Object.keys(RAW_WRAP).forEach(function (k) { RAW_UNWRAP[RAW_WRAP[k]] = k; });
  function encode(html) {
    html = html.replace(/<(dc-import)((?:[^>"']|"[^"]*"|'[^']*')*)\/>/gi, function (_, t, a) { return '<' + t + a + '></' + t + '>'; });
    html = html.replace(/(\s)([a-z]+[A-Z][A-Za-z0-9]*)(\s*=)/g, function (_, sp, name, eq) { return sp + CAMEL + name.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); }) + eq; });
    Object.keys(RAW_WRAP).forEach(function (real) { html = html.replace(new RegExp('(</?)' + real + '(?=[\\s>])', 'gi'), '$1' + RAW_WRAP[real]); });
    return html;
  }
  function kebabToCamel(s) { return s.replace(/-([a-z])/g, function (_, c) { return c.toUpperCase(); }); }
  function camelToKebab(s) { return s.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); }); }
  function cssToObj(css) {
    var o = {};
    css.split(';').forEach(function (decl) {
      var i = decl.indexOf(':'); if (i < 0) return;
      var prop = decl.slice(0, i).trim(); if (!prop) return;
      o[prop.indexOf('--') === 0 ? prop : kebabToCamel(prop)] = decl.slice(i + 1).trim();
    });
    return o;
  }
  // React-style DOM prop names -> real attribute names for Preact.
  var SVG_CAMEL = { viewBox: 1, preserveAspectRatio: 1, gradientUnits: 1, gradientTransform: 1, patternUnits: 1, patternTransform: 1, patternContentUnits: 1, textLength: 1, lengthAdjust: 1, markerWidth: 1, markerHeight: 1, markerUnits: 1, refX: 1, refY: 1, stdDeviation: 1, spreadMethod: 1, clipPathUnits: 1, maskUnits: 1, maskContentUnits: 1, startOffset: 1, baseFrequency: 1, numOctaves: 1, pathLength: 1, tableValues: 1, kernelMatrix: 1, keySplines: 1, keyTimes: 1, attributeName: 1, repeatCount: 1, calcMode: 1 };
  var DOM_RENAME = { className: 'class', htmlFor: 'for', autoFocus: 'autofocus', tabIndex: 'tabindex', readOnly: 'readonly', maxLength: 'maxlength', minLength: 'minlength', autoComplete: 'autocomplete', inputMode: 'inputmode', spellCheck: 'spellcheck', crossOrigin: 'crossorigin', srcSet: 'srcset', allowFullScreen: 'allowfullscreen', frameBorder: 'frameborder', playsInline: 'playsinline', autoPlay: 'autoplay', defaultValue: 'value', defaultChecked: 'checked', encType: 'enctype', noValidate: 'novalidate', referrerPolicy: 'referrerpolicy', fetchPriority: 'fetchpriority', contentEditable: 'contenteditable', dateTime: 'datetime' };

  // ---------- pseudo-class sheet (style-hover etc.) ----------
  var pseudoEl = null, pseudoCache = {}, pseudoN = 0;
  function importantify(css) {
    return css.split(';').map(function (d) { return d.trim(); }).filter(Boolean).map(function (d) { return /!\s*important$/i.test(d) ? d : d + ' !important'; }).join(';');
  }
  function pseudoClass(pseudo, css) {
    var k = pseudo + '|' + css;
    if (pseudoCache[k]) return pseudoCache[k];
    if (!pseudoEl) { pseudoEl = document.createElement('style'); pseudoEl.setAttribute('data-ppdc', 'pseudo'); document.head.appendChild(pseudoEl); }
    var cls = 'scp' + (pseudoN++).toString(36);
    var isEl = pseudo === 'before' || pseudo === 'after';
    var sel = isEl ? '.' + cls + '::' + pseudo : '.' + cls + ':' + pseudo;
    try { pseudoEl.sheet.insertRule(sel + '{' + (isEl ? css : importantify(css)) + '}', pseudoEl.sheet.cssRules.length); } catch (e) { console.warn('[ppdc] bad pseudo css', sel, css); }
    pseudoCache[k] = cls;
    return cls;
  }

  // ---------- compiler ----------
  function compile(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = encode(html);
    var builders = walkChildren(tpl.content);
    return function (vals) { return builders.map(function (b, i) { return b(vals || {}, i); }); };
  }
  function walkChildren(node) {
    var out = [];
    for (var i = 0; i < node.childNodes.length; i++) { var b = walk(node.childNodes[i]); if (b) out.push(b); }
    return out;
  }
  function walk(node) {
    if (node.nodeType === 3) return walkText(node);
    if (node.nodeType !== 1) return null;
    var tag = node.tagName.toLowerCase();
    if (tag === 'sc-for') return walkFor(node);
    if (tag === 'sc-if') return walkIf(node);
    if (tag === 'dc-import') return walkComponent(node);
    if (tag === 'helmet' || tag === 'sc-helmet' || tag === 'script') return null;
    return walkElement(node);
  }
  function walkText(node) {
    var txt = node.nodeValue || '';
    if (txt.indexOf('{{') < 0) {
      if (!txt.trim() && txt.indexOf(' ') < 0) return null;
      return function () { return txt; };
    }
    var parts = txt.split(/\{\{([\s\S]+?)\}\}/g);
    return function (vals) {
      return h(Fragment, null, parts.map(function (p, i) {
        if (!(i & 1)) return p;
        var v = resolve(vals, p);
        if (v === undefined || v === null || typeof v === 'boolean') return null;
        if (typeof v === 'object' && (v.type || Array.isArray(v))) return v;
        return h('span', { className: 'sc-interp' }, String(v));
      }));
    };
  }
  function walkFor(el) {
    var listGet = compileAttr(el.getAttribute('list') || ''), asName = el.getAttribute('as') || 'item';
    var kids = walkChildren(el);
    return function (vals) {
      var list = listGet(vals);
      if (!Array.isArray(list)) list = [];
      return h(Fragment, null, list.map(function (item, i) {
        var sub = Object.assign({}, vals); sub[asName] = item; sub.$index = i;
        return h(Fragment, { key: i }, kids.map(function (b, j) { return b(sub, j); }));
      }));
    };
  }
  function walkIf(el) {
    var valGet = compileAttr(el.getAttribute('value') || ''), kids = walkChildren(el);
    return function (vals) { return valGet(vals) ? h(Fragment, null, kids.map(function (b, j) { return b(vals, j); })) : null; };
  }
  function collect(el, kind) {
    var getters = [], pseudos = [];
    for (var i = 0; i < el.attributes.length; i++) {
      var name = el.attributes[i].name, value = el.attributes[i].value, key = name;
      if (key.indexOf(CAMEL) === 0) key = kebabToCamel(key.slice(CAMEL.length));
      if (key.indexOf('hint-') === 0) continue;
      if (key.indexOf('style-') === 0) { pseudos.push(pseudoClass(key.slice(6), value)); continue; }
      if (kind === 'component') { if (key.indexOf('-') >= 0) key = kebabToCamel(key); }
      getters.push([key, compileAttr(value)]);
    }
    return { getters: getters, pseudos: pseudos };
  }
  function walkComponent(el) {
    var name = el.getAttribute('name') || el.getAttribute('component') || '';
    el.removeAttribute('name'); el.removeAttribute('component');
    var styleRaw = el.getAttribute('style'); el.removeAttribute('style');
    var styleGet = styleRaw != null ? compileAttr(styleRaw) : null;
    var c = collect(el, 'component');
    return function (vals, key) {
      var props = { key: key, __name: name };
      if (styleGet) props.__hostStyle = hostPositionStyle(styleGet(vals));
      c.getters.forEach(function (g) { var v = g[1](vals); if (g[0] === 'dcProps') { if (v && typeof v === 'object') Object.assign(props, v); } else props[g[0]] = v; });
      return h(DC, props);
    };
  }
  var HOST_STYLE = { position: 1, left: 1, right: 1, top: 1, bottom: 1, inset: 1, width: 1, height: 1, zIndex: 1, transform: 1 };
  function hostPositionStyle(style) {
    var all = typeof style === 'string' ? cssToObj(style) : style; if (!all) return undefined;
    var out = {}, any = false; Object.keys(all).forEach(function (k) { if (HOST_STYLE[k]) { out[k] = all[k]; any = true; } });
    return any ? out : undefined;
  }
  function walkElement(el) {
    var tag = RAW_UNWRAP[el.localName] || el.localName;
    var isSvg = el.namespaceURI === 'http://www.w3.org/2000/svg';
    var c = collect(el, 'dom');
    var textInput = tag === 'textarea' || (tag === 'input' && !/^(checkbox|radio|file|submit|button)$/i.test(el.getAttribute('type') || ''));
    var kids = walkChildren(el);
    var attrs = c.getters.map(function (g) {
      var k = g[0];
      if (k === 'class') k = 'className';
      else if (k === 'for') k = 'htmlFor';
      if (/^on[a-z]/.test(k)) k = 'on' + k[2].toUpperCase() + k.slice(3);
      if (k === 'onChange' && textInput) k = 'onInput'; // React onChange fires per keystroke
      if (k !== 'className' && !/^on[A-Z]/.test(k) && /[A-Z]/.test(k)) {
        if (DOM_RENAME[k]) k = DOM_RENAME[k];
        else if (!(isSvg && SVG_CAMEL[k])) k = camelToKebab(k);
      }
      return [k, g[1]];
    });
    return function (vals, key) {
      var props = { key: key };
      for (var i = 0; i < attrs.length; i++) {
        var k = attrs[i][0], v = attrs[i][1](vals);
        if (k === 'style' && typeof v === 'string') v = cssToObj(v);
        if ((k === 'value' || k === 'checked') && v === undefined) v = k === 'checked' ? false : '';
        props[k] = v;
      }
      if (c.pseudos.length) props.className = [props.className].concat(c.pseudos).filter(Boolean).join(' ');
      return h(tag, props, kids.map(function (b, j) { return b(vals, j); }));
    };
  }

  // ---------- components ----------
  var registry = {};
  function DCLogic(props) { this.props = props || {}; this.state = {}; this.__host = null; }
  DCLogic.prototype.setState = function (u, cb) { this.__host && this.__host.__set(u, cb); };
  DCLogic.prototype.forceUpdate = function () { this.__host && this.__host.forceUpdate(); };
  DCLogic.prototype.componentDidMount = function () {};
  DCLogic.prototype.componentDidUpdate = function () {};
  DCLogic.prototype.componentWillUnmount = function () {};
  DCLogic.prototype.renderVals = function () { return {}; };

  function userProps(p) { var o = {}; Object.keys(p).forEach(function (k) { if (k.indexOf('__') !== 0 && k !== 'children' && k !== 'key') o[k] = p[k]; }); return o; }

  function DC(props) { P.Component.call(this, props); var r = registry[props.__name]; var L = (r && r.Logic) || DCLogic; this.logic = new L(withDefaults(props.__name, userProps(props))); this.logic.__host = this; }
  DC.prototype = Object.create(P.Component.prototype);
  DC.prototype.constructor = DC;
  DC.prototype.__set = function (u, cb) {
    var prev = this.logic.state, patch = typeof u === 'function' ? u(prev) : u;
    this.logic.state = Object.assign({}, prev, patch);
    this.setState({ __v: (this.state.__v || 0) + 1 }, cb);
  };
  DC.prototype.componentDidMount = function () { try { this.logic.componentDidMount(); } catch (e) { console.error(e); } };
  DC.prototype.componentDidUpdate = function (prev) { this.logic.props = withDefaults(this.props.__name, userProps(this.props)); try { this.logic.componentDidUpdate(prev); } catch (e) { console.error(e); } };
  DC.prototype.componentWillUnmount = function () { try { this.logic.componentWillUnmount(); } catch (e) { console.error(e); } };
  DC.prototype.render = function (props) {
    var r = registry[props.__name];
    if (!r) return h('div', { className: 'sc-host', 'data-sc-name': props.__name });
    if (!r.render) r.render = compile(r.html);
    var up = withDefaults(props.__name, userProps(props));
    this.logic.props = up;
    var vals = up;
    try { vals = Object.assign({}, up, this.logic.renderVals() || {}); } catch (e) { console.error('[ppdc] ' + props.__name + '.renderVals()', e); }
    return h('div', { className: 'sc-host', style: props.__hostStyle, 'data-sc-name': props.__name }, r.render(vals));
  };
  function withDefaults(name, up) {
    var r = registry[name]; if (!r || !r.defaults) return up;
    var o = Object.assign({}, r.defaults); Object.keys(up).forEach(function (k) { if (up[k] !== undefined) o[k] = up[k]; }); return o;
  }

  var ReactShim = { createRef: P.createRef, createElement: h, Fragment: Fragment };

  window.PPDC = {
    resolve: resolve,
    /** Register a component: html template, logic factory (DCLogic, React) => Class, default props. */
    define: function (name, html, logicFactory, defaults) {
      var Logic = null;
      if (logicFactory) { try { Logic = logicFactory(DCLogic, ReactShim); } catch (e) { console.error('[ppdc] logic for ' + name, e); } }
      var rw = window.PPDC_REWRITE || {};
      Object.keys(rw).forEach(function (k) { html = html.split(k).join(rw[k]); });
      registry[name] = { html: html, Logic: Logic, defaults: defaults || null, render: null };
    },
    mount: function (name, el, props) {
      var p = Object.assign({ __name: name }, props || {});
      P.render(h(DC, p), el);
    },
    h: h
  };
})();
